import { motion, useScroll, useTransform, useInView, useMotionValueEvent } from 'framer-motion';
import { useLayoutEffect, useRef, useState } from 'react';

// Helper component for highlighted text - more visible underline style
const HighlightText = ({ children }: { children: React.ReactNode }) => (
  <span 
    className="relative inline font-medium"
    style={{
      color: 'hsl(357 99% 55%)',
    }}
  >
    {children}
  </span>
);

const steps = [
  {
    id: 1,
    label: 'SCHRITT 1',
    title: 'Kennenlernen & Zieldefinition',
    description: (
      <>
        In einem ersten <HighlightText>unverbindlichen Gespräch</HighlightText> lernen wir dich kennen. Wir besprechen deine gewünschten Themen und <HighlightText>Ziele</HighlightText>.
      </>
    ),
  },
  {
    id: 2,
    label: 'SCHRITT 2',
    title: 'Analyse & Strategie',
    description: (
      <>
        Wir analysieren deine <HighlightText>finanzielle Situation</HighlightText> und identifizieren <HighlightText>Optimierungspotenziale</HighlightText>. Basierend auf deiner Lebenssituation erstellen wir eine maßgeschneiderte Strategie.
      </>
    ),
  },
  {
    id: 3,
    label: 'SCHRITT 3',
    title: '100% digitale Umsetzung',
    description: (
      <>
        Unsere Zusammenarbeit ist ein Versprechen: Du musst dich um nichts kümmern. Die gesamte Abwicklung läuft <HighlightText>100% digital</HighlightText> ab – <HighlightText>kein Papierkram</HighlightText>.
      </>
    ),
  },
  {
    id: 4,
    label: 'SCHRITT 4',
    title: 'Langfristige Begleitung',
    description: (
      <>
        Als dein <HighlightText>langfristiger Partner</HighlightText> behalten wir deine Entwicklung im Blick und passen die Planung flexibel an deine jeweilige Lebensphase an. Durch regelmäßigen Austausch bleibst du <HighlightText>immer up to date</HighlightText>.
      </>
    ),
  },
];

const TimelineStep = ({ 
  step, 
  index, 
  isLeft,
  active,
  hit,
  dotRef,
}: { 
  step: typeof steps[0]; 
  index: number; 
  isLeft: boolean;
  active: boolean;
  hit: boolean;
  dotRef: (el: HTMLDivElement | null) => void;
}) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { 
    once: false, 
    margin: '-45% 0px -45% 0px' 
  });

  return (
    <div ref={ref} className="relative">
      {/* Desktop Layout */}
      <div className="hidden md:grid md:grid-cols-[1fr_80px_1fr] items-start">
        {/* Left Content */}
        <div className={`${isLeft ? 'pr-8' : ''}`}>
          {isLeft && (
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0.15, x: -20 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-right space-y-4"
            >
              <span 
                className="inline-block text-xs font-semibold tracking-[0.2em] transition-colors duration-500"
                style={{ color: isInView ? 'hsl(357 99% 45%)' : 'hsl(220 10% 40%)' }}
              >
                {step.label}
              </span>
              <h3 
                className="text-xl lg:text-2xl font-serif font-normal leading-snug transition-colors duration-500"
                style={{ color: isInView ? 'hsl(0 0% 100%)' : 'hsl(220 10% 55%)' }}
              >
                {step.title}
              </h3>
              <p 
                className="text-sm lg:text-[15px] leading-relaxed max-w-sm ml-auto transition-colors duration-500"
                style={{ color: isInView ? 'hsl(220 10% 70%)' : 'hsl(220 10% 40%)' }}
              >
                {step.description}
              </p>
            </motion.div>
          )}
        </div>

        {/* Center Timeline - just the marker, line is handled globally */}
        <div className="flex justify-center relative h-[200px]">
          {/* Marker with pulse animation */}
          <motion.div
            className="relative z-10"
            animate={isInView ? { scale: 1 } : { scale: 0.6 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            {/* Main dot */}
            <div
              ref={(el) => {
                // Only use desktop dot for measurement when desktop layout is visible
                if (el && window.innerWidth >= 768) dotRef(el);
              }}
              className="w-4 h-4 rounded-full transition-all duration-500 relative z-10"
              style={{ 
                backgroundColor: hit ? 'hsl(var(--primary))' : 'hsl(220 15% 30%)',
                boxShadow: active && hit ? '0 0 10px hsl(var(--primary) / 0.55)' : 'none',
              }}
            />
          </motion.div>
        </div>

        {/* Right Content */}
        <div className={`${!isLeft ? 'pl-8' : ''}`}>
          {!isLeft && (
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0.15, x: 20 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="text-left space-y-4"
            >
              <span 
                className="inline-block text-xs font-semibold tracking-[0.2em] transition-colors duration-500"
                style={{ color: isInView ? 'hsl(357 99% 45%)' : 'hsl(220 10% 40%)' }}
              >
                {step.label}
              </span>
              <h3 
                className="text-xl lg:text-2xl font-serif font-normal leading-snug transition-colors duration-500"
                style={{ color: isInView ? 'hsl(0 0% 100%)' : 'hsl(220 10% 55%)' }}
              >
                {step.title}
              </h3>
              <p 
                className="text-sm lg:text-[15px] leading-relaxed max-w-sm transition-colors duration-500"
                style={{ color: isInView ? 'hsl(220 10% 70%)' : 'hsl(220 10% 40%)' }}
              >
                {step.description}
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden flex gap-5">
        <div className="flex flex-col items-center">
          <motion.div
            className="relative"
            animate={isInView ? { scale: 1 } : { scale: 0.6 }}
            transition={{ duration: 0.3 }}
          >
            <div
              ref={(el) => {
                // Only use mobile dot for measurement when mobile layout is visible
                if (el && window.innerWidth < 768) dotRef(el);
              }}
              className="w-3.5 h-3.5 rounded-full flex-shrink-0 transition-all duration-400 relative z-10"
              style={{ 
                backgroundColor: hit ? 'hsl(var(--primary))' : 'hsl(220 15% 35%)',
                boxShadow: active && hit ? '0 0 8px hsl(var(--primary) / 0.55)' : 'none',
              }}
            />
          </motion.div>
          {/* Mobile connecting spacer - no visible line here, global overlay handles progress line */}
          <div className="flex-1 min-h-[120px]" />
        </div>

        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0.2, x: 12 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="flex-1 pb-8 space-y-2.5"
        >
          <span 
            className="inline-block text-[10px] font-semibold tracking-[0.2em] transition-colors duration-500"
            style={{ color: isInView ? 'hsl(357 99% 45%)' : 'hsl(220 10% 40%)' }}
          >
            {step.label}
          </span>
          <h3 
            className="text-lg font-serif font-normal leading-snug transition-colors duration-500"
            style={{ color: isInView ? 'hsl(0 0% 100%)' : 'hsl(220 10% 55%)' }}
          >
            {step.title}
          </h3>
          <p 
            className="text-sm leading-relaxed transition-colors duration-500"
            style={{ color: isInView ? 'hsl(220 10% 68%)' : 'hsl(220 10% 40%)' }}
          >
            {step.description}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

const ProcessTimelineSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const lineRef = useRef<HTMLDivElement>(null);
  const mobileLineRef = useRef<HTMLDivElement>(null);
  const dotRefs = useRef<(HTMLDivElement | null)[]>([]);

  const thresholdsRef = useRef<number[]>([]);
  const hitIndexRef = useRef(-1);

  const [hitIndex, setHitIndex] = useState(-1);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  
  // Track scroll progress relative to the section. End offset adjusted so progress reaches 100%
  // when the section bottom (plus the 160px line extension) leaves the viewport.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start center", "end 30%"]
  });

  // Keep dot timing exact: fill follows scroll 1:1 until the last dot; only the very last tail is eased.
  const fillProgress = useTransform(scrollYProgress, (v) => {
    const lastDot = thresholdsRef.current[steps.length - 1] ?? 0.88;
    if (v <= lastDot) return v;

    const t = (v - lastDot) / (1 - lastDot);
    const eased = 1 - Math.pow(1 - t, 3); // slightly stronger ease-out => closes the remaining gap
    return lastDot + eased * (1 - lastDot);
  });

  // Measure real dot positions so dots light up exactly when the fill reaches them.
  useLayoutEffect(() => {
    const measure = () => {
      const isMobile = window.innerWidth < 768;
      const lineEl = isMobile ? mobileLineRef.current : lineRef.current;
      if (!lineEl || !timelineRef.current) return;

      const lineRect = lineEl.getBoundingClientRect();
      const height = lineRect.height;
      if (!height) return;

      const nextThresholds: number[] = [];
      for (let i = 0; i < steps.length; i++) {
        const dot = dotRefs.current[i];
        if (!dot) continue;
        const r = dot.getBoundingClientRect();
        const centerY = (r.top - lineRect.top) + r.height / 2;
        nextThresholds[i] = Math.min(1, Math.max(0, centerY / height));
      }

      if (nextThresholds.filter((x) => typeof x === 'number' && !isNaN(x)).length === steps.length) {
        thresholdsRef.current = nextThresholds;
      }
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    if (timelineRef.current) ro.observe(timelineRef.current);
    if (sectionRef.current) ro.observe(sectionRef.current);
    window.addEventListener('resize', measure);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  // Update hit/active indices only when the FILL crosses a dot threshold.
  useMotionValueEvent(fillProgress, 'change', (v) => {
    const th = thresholdsRef.current;
    if (th.length !== steps.length) return;

    let nextHit = -1;
    for (let i = 0; i < th.length; i++) {
      if (v >= th[i]) nextHit = i;
    }

    if (nextHit !== hitIndexRef.current) {
      hitIndexRef.current = nextHit;
      setHitIndex(nextHit);
      setActiveStepIndex(Math.max(0, nextHit));
    }
  });

  // Transform scroll progress to line height percentage
  const lineProgress = fillProgress;

  return (
    <section id="process" ref={sectionRef} className="relative overflow-hidden">
      {/* Elegant gradient transition */}
      <div 
        className="absolute inset-0"
        style={{ 
          background: `linear-gradient(180deg, 
            hsl(var(--background)) 0%,
            hsl(220 15% 90%) 6%,
            hsl(220 18% 65%) 14%,
            hsl(220 20% 35%) 28%,
            hsl(222 25% 18%) 45%,
            hsl(222 28% 12%) 70%,
            hsl(222 30% 10%) 100%
          )`
        }}
      />


      {/* Content */}
      <div className="relative pt-36 pb-0 md:pt-44 md:pb-0">
        <div className="container max-w-[950px] px-6">
          
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="text-center mb-24 md:mb-28"
          >
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif font-normal tracking-tight text-white">
              So läuft unsere{' '}
              <span className="italic text-primary">Zusammenarbeit</span>{' '}
              ab
            </h2>
          </motion.div>

          {/* Timeline with continuous vertical line */}
          <div ref={timelineRef} className="relative">
            {/* Desktop: background line - extends 160px beyond container into CTA */}
            <div 
              className="hidden md:block absolute left-1/2"
              ref={lineRef}
              style={{ 
                backgroundColor: 'hsl(220 15% 22%)',
                width: '1px',
                transform: 'translateX(-50%)',
                top: 0,
                bottom: -160,
              }}
            />
            
            {/* Desktop: progress fill - extends into CTA, exact same position as background */}
            <motion.div
              className="hidden md:block absolute left-1/2"
              style={{ 
                backgroundColor: 'hsl(var(--primary))',
                width: '1px',
                transform: 'translateX(-50%)',
                top: 0,
                bottom: -160,
                scaleY: lineProgress,
                transformOrigin: 'top center',
              }}
            />

            {/* Mobile: Single background line - positioned to align with dot centers (container px-6 = 24px, dot center at ~7px = 31px total) */}
            <div 
              className="md:hidden absolute"
              ref={mobileLineRef}
              style={{ 
                backgroundColor: 'hsl(220 15% 25%)',
                left: '7px',
                width: '2px',
                top: 0,
                bottom: -120,
              }}
            />
            
            {/* Mobile: progress fill - exact same position to create single line illusion */}
            <motion.div
              className="md:hidden absolute"
              style={{ 
                backgroundColor: 'hsl(var(--primary))',
                left: '7px',
                width: '2px',
                top: 0,
                bottom: -120,
                scaleY: lineProgress,
                transformOrigin: 'top',
              }}
            />

            <div className="space-y-0 md:space-y-0">
              {steps.map((step, index) => (
                // A dot is considered "hit" when the fill reaches its vertical midpoint position
                // Dot positions align with 12.5%, 37.5%, 62.5%, 87.5% of the section progress.
                // This keeps the glow in sync with the visual fill.
                <TimelineStep
                  key={step.id}
                  step={step}
                  index={index}
                  isLeft={index % 2 === 0}
                  active={index === activeStepIndex}
                  hit={hitIndex >= index}
                  dotRef={(el) => {
                    dotRefs.current[index] = el;
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessTimelineSection;
